import { createStore } from 'vuex'
import { normalizeEnabledModules, type EnabledModules, type ModuleCode } from '@/config/modules'

export type AuthUser = {
  id: string
  owner_id: string
  email: string
  full_name: string
  organization?: string | null
  is_accounting_firm?: boolean
  accountant_owner_id?: string | null
  can_manage_account_users?: boolean
  enabled_modules?: EnabledModules
  receipt_profile?: {
    business_name?: string | null
    legal_name?: string | null
    business_subtitle?: string | null
    address_line_1?: string | null
    address_line_2?: string | null
    phone?: string | null
    tax_condition?: string | null
    cuit?: string | null
    iibb?: string | null
    dri?: string | null
    start_date?: string | null
    point_of_sale?: string | null
    theme_preference?: string | null
    arca_emision_enabled?: boolean | null
    afip_produccion_cert_path?: string | null
    afip_produccion_key_path?: string | null
    afip_cuit?: string | null
    arca_scraper_cuit?: string | null
    arca_scraper_clave_fiscal_configurada?: boolean | null
    arca_scraper_es_representante?: boolean | null
    arca_scraper_representado_cuit?: string | null
    afip_clave_fiscal_configurada?: boolean | null
    remito_r_point_of_sale?: string | null
    remito_r_cai?: string | null
    remito_r_cai_vto?: string | null
    remito_r_cai_desde?: string | null
    remito_r_cai_hasta?: string | null
    liquidacion_flete_point_of_sale?: string | null
    liquidacion_flete_cai?: string | null
    liquidacion_flete_cai_vto?: string | null
    liquidacion_flete_cai_desde?: string | null
    liquidacion_flete_cai_hasta?: string | null
  }
  role: string
  is_active: boolean
  is_verified: boolean
  created_at: string
}

type RootState = {
  isAuthenticated: boolean
  token: string | null
  role: string | null
  user: AuthUser | null
  selectedOwnerId: string | null
}

function defaultOwnerId (user: AuthUser | null): string | null {
  return user?.owner_id || user?.id || null
}

function canSwitchOwnerScope (user: AuthUser | null, role: string | null): boolean {
  return role !== 'admin' && Boolean(user?.is_accounting_firm)
}

function resolveSelectedOwnerId (
  user: AuthUser | null,
  role: string | null,
  requestedOwnerId: string | null
): string | null {
  const fallbackOwnerId = defaultOwnerId(user)
  if (!canSwitchOwnerScope(user, role)) {
    return fallbackOwnerId
  }
  return requestedOwnerId || fallbackOwnerId
}

function decodeJwtPayload (token: string | null): Record<string, any> | null {
  if (!token) return null
  const parts = token.split('.')
  if (parts.length < 2) return null
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')
    return JSON.parse(window.atob(padded))
  } catch {
    return null
  }
}

function loadStoredUser (): AuthUser | null {
  const raw = localStorage.getItem('auth_user')
  if (!raw) return null

  try {
    return JSON.parse(raw) as AuthUser
  } catch {
    localStorage.removeItem('auth_user')
    return null
  }
}

const initialToken = localStorage.getItem('access_token')
const initialPayload = decodeJwtPayload(initialToken)
const initialUser = loadStoredUser()
const initialSelectedOwnerId = localStorage.getItem('selected_owner_id')
const initialRole = typeof initialPayload?.role === 'string' ? initialPayload.role : initialUser?.role || null

export default createStore({
  state: (): RootState => ({
    isAuthenticated: localStorage.getItem('logueado') === 'true',
    token: initialToken,
    role: initialRole,
    user: initialUser,
    selectedOwnerId: resolveSelectedOwnerId(initialUser, initialRole, initialSelectedOwnerId)
  }),
  getters: {
    isAuthenticated: (state) => state.isAuthenticated,
    isAdmin: (state) => state.role === 'admin',
    isAccountingFirm: (state) => Boolean(state.user?.is_accounting_firm),
    hasModule: (state) => (module: ModuleCode) =>
      state.role === 'admin' || normalizeEnabledModules(state.user?.enabled_modules)[module],
    hasContabilidadModule: (state) =>
      state.role === 'admin' || normalizeEnabledModules(state.user?.enabled_modules).contabilidad,
    canAccessAmortizaciones: (state) => state.role === 'admin' || Boolean(state.user?.is_accounting_firm),
    canSwitchOwnerScope: (state) => canSwitchOwnerScope(state.user, state.role),
    currentUser: (state) => state.user,
    selectedOwnerId: (state) => state.selectedOwnerId
  },
  mutations: {
    setAuth (state, payload: { token: string, user: AuthUser }) {
      const jwtPayload = decodeJwtPayload(payload.token)
      state.isAuthenticated = true
      state.token = payload.token
      state.role = typeof jwtPayload?.role === 'string' ? jwtPayload.role : payload.user.role
      state.user = payload.user
      state.selectedOwnerId = resolveSelectedOwnerId(
        payload.user,
        state.role,
        localStorage.getItem('selected_owner_id')
      )
      localStorage.setItem('logueado', 'true')
      localStorage.setItem('access_token', payload.token)
      localStorage.setItem('auth_user', JSON.stringify(payload.user))
      if (state.selectedOwnerId) {
        localStorage.setItem('selected_owner_id', state.selectedOwnerId)
      } else {
        localStorage.removeItem('selected_owner_id')
      }
    },
    setCurrentUser (state, user: AuthUser | null) {
      state.user = user
      if (user) {
        state.role = user.role || state.role
        localStorage.setItem('auth_user', JSON.stringify(user))
        state.selectedOwnerId = resolveSelectedOwnerId(user, state.role, state.selectedOwnerId)
        if (state.selectedOwnerId) {
          localStorage.setItem('selected_owner_id', state.selectedOwnerId)
        } else {
          localStorage.removeItem('selected_owner_id')
        }
      } else {
        localStorage.removeItem('auth_user')
      }
    },
    setSelectedOwnerId (state, ownerId: string | null) {
      state.selectedOwnerId = resolveSelectedOwnerId(state.user, state.role, ownerId)
      if (state.selectedOwnerId) {
        localStorage.setItem('selected_owner_id', state.selectedOwnerId)
      } else {
        localStorage.removeItem('selected_owner_id')
      }
    },
    clearAuth (state) {
      state.isAuthenticated = false
      state.token = null
      state.role = null
      state.user = null
      state.selectedOwnerId = null
      localStorage.removeItem('logueado')
      localStorage.removeItem('access_token')
      localStorage.removeItem('auth_user')
      localStorage.removeItem('selected_owner_id')
    }
  },
  actions: {},
  modules: {
  }
})
